import { Button, Card, Col, Form, Input, Row, Select, Tooltip } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import { General } from "../../../../util/General";
import { formItemLayout, inputLayout2, tailFormItemLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import FormInputRules from "../../../../util/FormInputRules";
import Loading from "../../../common/Loading";
import { UserRoleDATA } from "../../../../constants/DatabaseConstants/AuthConstants";
import { createHandleEditKeyPress, createHandleKeyPress } from "../../../../util/handleKeyPress";
import { useWebUserForm } from "./index.hook";
import styles from "../../index.module.less";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";

const { Option } = Select;

const WebUserForm = () => {
  /* CONSTANTS */
  const config = useWebUserForm();
  const checkCustomerLength = config.customerMasterList?.length !== config.customerLocationData?.length || config.customerLocationData?.length === 0;

  const renderSelectedCustomerAndLocationList = () => {
    return config.customerLocationData?.map((item, index: number) => {
      return (
        <div key={index}>
          <Form.Item
            {...inputLayout2}
            label={
              <span>
                <span className={styles["form-required-field"]}>*</span> Customer
              </span>
            }
            rules={[FormInputRules.requiredFieldSelectRule("customer")]}
          >
            <div className={styles["customer-location-container"]}>
              <Select disabled={true} placeholder="Select Customer" value={item?.customer_id} onChange={config.handleCustomerChange} showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect}>
                <Option value={item?.customer_id}>{item?.customer_name}</Option>
              </Select>

              <Tooltip title="Delete Customer">{!config.readEnabled && <Button className="gx-mb-0" type="text" icon={<DeleteOutlined className={styles["delete-customer-icon"]} />} onClick={() => config.handleRemoveCustomerLocationData(item.customer_id)} />}</Tooltip>
            </div>
          </Form.Item>

          <Form.Item
            {...inputLayout2}
            label={
              <span>
                <span className={styles["form-required-field"]}>*</span> Customer Location
              </span>
            }
            rules={[FormInputRules.requiredFieldSelectRule("customer location")]}
          >
            <Select mode="multiple" disabled={config.readEnabled} value={item?.customer_location_list} placeholder="Select Customer Location" onChange={(selectedLocations) => config.handleChangeLocation(selectedLocations, item?.customer_id)} showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect}>
              {[...config?.formattedLocationList, ...config?.locationMasterList]
                ?.reduce((acc: FixMeLater[], location: FixMeLater) => {
                  const isUnique = !acc.some((loc) => loc.id === location.id);
                  if (isUnique) {
                    acc.push(location);
                  }
                  return acc;
                }, [])
                ?.filter((location) => location?.customer_id === item?.customer_id)
                ?.map((location: FixMeLater) => {
                  return (
                    <Option value={location?.id} key={location?.id}>
                      {location?.location_name}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </div>
      );
    });
  };

  const renderCustomerList = () => {
    const selectedCustomerIds = config.customerLocationData?.map((data) => data?.customer_id);

    if (config.customerMasterList?.length !== config.customerLocationData?.length || config.customerLocationData?.length === 0) {
      return (
        <Form.Item
          {...inputLayout2}
          label={
            <span>
              <span className={styles["form-required-field"]}>*</span> Customer
            </span>
          }
          rules={[FormInputRules.requiredFieldSelectRule("customer")]}
        >
          <Select showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} disabled={config.readEnabled} placeholder="Select Customer" onChange={config.handleCustomerChange}>
            {config.customerMasterList
              ?.filter((customer: FixMeLater) => !selectedCustomerIds?.includes(customer?.id))
              ?.map((customer: FixMeLater) => (
                <Option value={customer?.id} key={customer?.id}>
                  {customer?.customer_name}
                </Option>
              ))}
          </Select>
        </Form.Item>
      );
    }
  };

  return (
    <div>
      <Card className="gx-card card--height" title={` ${config.id && config.view ? "view" : config.id ? "Update" : "Add"} User`}>
        {config.loading && <Loading />}

        {!config.loading && (
          <Form
            {...formItemLayout}
            onValuesChange={(changedValues) => {
              const fieldName = Object.keys(changedValues)[0];
              config.form.validateFields([fieldName]);
            }}
            validateTrigger="onBlur"
            colon={false}
            form={config.form}
            onFinish={config.handleSubmit}
            scrollToFirstError
            initialValues={{ is_active: true }}
          >
            <Row gutter={24}>
              <Col xs={24} sm={24} md={24} lg={11}>
                <Form.Item {...inputLayout2} name="role_id" label="Role" rules={[FormInputRules.requiredFieldSelectRule("role")]}>
                  <Select showSearch allowClear getPopupContainer={(trigger) => trigger.parentElement} dropdownStyle={{ maxHeight: "200px", overflowY: "auto" }} filterOption={General.searchFilterOnSelect} placeholder="Select role" disabled={config.readEnabled}>
                    {/* WP Admin role skip to not select WP-Admin role */}
                    {config.roleList
                      ?.filter((item) => {
                        if ((config.id && config.view) || config?.roleName === UserRoleDATA.PARI_ADMIN) {
                          return true;
                        }
                        return item?.rolename !== UserRoleDATA.PARI_ADMIN;
                      })
                      ?.map((item) => (
                        <Option value={item.id} key={item.id}>
                          {item.rolename}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item {...inputLayout2} name="username" label="Username" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("username"), FormInputRules.minLengthRule(2, "username")]}>
                  <Input name="username" disabled={config.readEnabled} />
                </Form.Item>

                <Form.Item {...inputLayout2} name="first_name" label="First Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("first name"), FormInputRules.ValidateFirstAndLastName(), FormInputRules.minLengthRule(1, "first name")]}>
                  <Input name="first_name" disabled={config.readEnabled} />
                </Form.Item>

                <Form.Item {...inputLayout2} name="last_name" label="Last Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("last name"), FormInputRules.ValidateFirstAndLastName()]}>
                  <Input name="last_name" disabled={config.readEnabled} />
                </Form.Item>

                <Form.Item {...inputLayout2} name="email_address" label="Email Address" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.EmailValidationRule()]}>
                  <Input name="email_address" disabled={config.readEnabled} />
                </Form.Item>

                {config.id ? (
                  <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidationRule()]}>
                    <Input name="mobile_number" disabled={config.readEnabled} onKeyPress={createHandleEditKeyPress()} />
                  </Form.Item>
                ) : (
                  <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidation2Rule()]}>
                    <Input prefix="+91" name="mobile_number" disabled={config.readEnabled} onKeyPress={createHandleKeyPress()} />
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} sm={24} md={24} lg={13}>
                {/* Customer Location Dynamic field. */}
                {renderSelectedCustomerAndLocationList()}

                {/* Customer Field */}
                {config.addCustomer && renderCustomerList()}

                {/* functional buttons. */}
                {!config.readEnabled && !config.addCustomer && (
                  <Form.Item {...inputLayout2} label={<></>}>
                    {checkCustomerLength && (
                      <div>
                        <Button className="gx-mb-0 button-gradiant" onClick={config.openCustomerSelection}>
                          {"Add Customer"}
                        </Button>
                      </div>
                    )}

                    {!checkCustomerLength && <Button className="gx-mb-0 gx-text-red">{"No more Customer to Select"}</Button>}
                  </Form.Item>
                )}
              </Col>
            </Row>
            {!config.readEnabled && (
              <Form.Item {...tailFormItemLayout} className="form-button gx-mr-4">
                <Button className="gx-mb-0 button-gradiant" htmlType="submit">
                  {config.id && config.saving ? <CircularProgressBar /> : config.id ? "Update" : config.saving ? <CircularProgressBar /> : "Submit"}
                </Button>
              </Form.Item>
            )}

            <Form.Item className="form-button form-button-pr ">
              <Button className="gx-mb-0 button-gradiant" onClick={config.handleBack}>
                Back
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default WebUserForm;
